import React from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { Add } from '@mui/icons-material';
import { zodResolver } from '@hookform/resolvers/zod';
import { InputField, SelectField } from 'components/form';
import { Box, Button, Stack, Typography, Alert } from '@mui/material';
import { PaymentType, SaleType } from '@trnsact/trnsact-shared-types';
import { convertEnumToSelectOptions } from 'utils/selectOptionsHelper';

import { useChargesConfiguration } from '../../api';
import { dealSheetConfigurationMapper } from '../../mappers';

const salesCenterFormSchema = z
  .object({
    saleType: z.string().trim(),
    location: z.string().trim(),
    paymentType: z.string().trim(),
    equipmentType: z.string().trim(),
  })
  .refine(data => Object.values(data).some(value => value.trim() !== ''), {
    path: ['general'],
    message: 'At least one field must be filled in',
  });

export const SalesCenterForm = () => {
  const { isConfigurationCreating, handleCreateConfiguration } = useChargesConfiguration();

  const { control, handleSubmit, formState, reset } = useForm({
    mode: 'all',
    resolver: zodResolver(salesCenterFormSchema),
    defaultValues: { saleType: '', location: '', paymentType: '', equipmentType: '' },
  });

  const handleSubmitDealRules = async (formData: Record<string, any>) => {
    const dealRules = dealSheetConfigurationMapper.processSalesCenterFormToDealRules(formData);

    await handleCreateConfiguration({ charges: [], dealRules }, api => ({
      refetchQueries: [api.getConfigs],
      awaitRefetchQueries: true,
    }));

    reset();
  };

  return (
    <Stack className="salesCenterCard" gap="1rem">
      <Typography variant="h6" component="span">
        Deal Rules
      </Typography>

      <Alert severity="info">
        <Typography>
          Create workflow rules by specifying one or more criteria. You can create rules for specific customer types,
          locations, equipment types, or any combination. At least one field must be selected.
        </Typography>
      </Alert>

      {formState.errors?.general && (
        <Alert severity="error">
          <Typography>{formState.errors.general.message}</Typography>
        </Alert>
      )}

      <Box sx={{ gap: '1rem', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)' }}>
        <InputField
          label="Equipment type"
          control={control}
          name="equipmentType"
          inputProps={{
            variant: 'outlined',
          }}
        />

        <SelectField
          name="saleType"
          label="Sale type"
          control={control}
          variant="outlined"
          options={convertEnumToSelectOptions(SaleType)}
        />

        <SelectField
          control={control}
          name="paymentType"
          variant="outlined"
          label="Payment type"
          options={convertEnumToSelectOptions(PaymentType)}
        />

        <InputField
          label="Location (City, State, County, Country)"
          control={control}
          name="location"
          inputProps={{
            variant: 'outlined',
          }}
        />
      </Box>

      <Button
        variant="contained"
        startIcon={<Add />}
        sx={{ alignSelf: 'flex-end' }}
        onClick={handleSubmit(handleSubmitDealRules)}
        loading={formState.isSubmitting || isConfigurationCreating}
      >
        Add Rule
      </Button>
    </Stack>
  );
};
